import { useState, useEffect } from 'react'
import { useRecoilState } from 'recoil'

import { productDetailState } from 'src/actions/products'
import useQuantity from './useQuantity'
import { shopifyCartState } from 'src/store/cartAtom'
import { removePromoCode } from 'src/actions/cart'

const useCart = () => {
  const [cartState, setCartState] = useRecoilState(shopifyCartState)
  const [products] = useRecoilState(productDetailState)

  const [checkoutUrl, setCheckoutUrl] = useState('')
  const [subscriptionSavings, setSubscriptionSavings] = useState(0)
  const [discounts, setDiscounts] = useState<{ code: string; amount: string }[]>([])
  const [discountSavings, setDiscountSavings] = useState(0)

  const { fetching, updating, cart } = cartState
  const cartId = cart?.id || ''
  const updatedAt = cart?.updatedAt || ''
  const items = cart?.lines.edges || []
  const discountAllocations = cart?.discountAllocations || []
  const discountCodes = cart?.discountCodes || []

  const actualTotal = Number(cart?.cost?.subtotalAmount?.amount) || 0
  const grossTotal = actualTotal + (subscriptionSavings || discountSavings)

  const freeShipping = grossTotal >= 40 // free shipping only above $40

  const loading = products.fetching || fetching

  const [, quantity] = useQuantity(items)

  // Tracks checkoutUrls
  useEffect(() => {
    if (!cartId) return
    const url = cart.checkoutUrl
    setCheckoutUrl(url)
  }, [cartId, updatedAt])

  // Removes certain promo codes each time a user visits site
  useEffect(() => {
    if (!cartId) return
    const removablePromoCodes = ['CALMTV', 'TVBUNDLE', 'SAVE50'] // Remove these if on main site
    const currentCode = discounts.length > 0 ? discounts[0].code : ''
    if (removablePromoCodes.includes(currentCode)) {
      removePromoCode(cartState, setCartState)
    }
  }, [cartId, updatedAt])

  // Calculates savings from subscriptions
  useEffect(() => {
    if (!cartId || loading) return
    setSubscriptionSavings(0)
    items.forEach((lineItem: any) => {
      const node = lineItem?.node || {}
      const isSubscription = Boolean(node?.sellingPlanAllocation)
      if (!isSubscription) return

      const fullVariantId = node.merchandise.id
      const variantIdBreakdown = fullVariantId.split('/')
      const variantId = variantIdBreakdown[variantIdBreakdown.length - 1]
      const variantDetail = products?.byVariantId[variantId]
      const discount = variantDetail?.pricing?.subscription?.priceDiscount
      const itemSavings = discount * node?.quantity
      setSubscriptionSavings((prevSavings) => prevSavings + itemSavings)
    })
  }, [loading, cartId, updatedAt])

  // Calculates savings from promo codes
  useEffect(() => {
    if (!cartId) return
    setDiscountSavings(0)
    // Since discountCodes & discountAllocations are separate, let's assume there's only 1 code
    // and we will sum up the discountAllocations for the value
    const code =
      discountCodes.filter((code) => code.applicable).length > 0 ? discountCodes[0].code : null
    if (!code) {
      setDiscounts([])
      return
    }

    const amount = discountAllocations.reduce((total: number, allocation: any) => {
      const discountedAmount = allocation.discountedAmount.amount || 0
      return total + Number(discountedAmount)
    }, 0)

    const formattedDiscounts = [
      { code, amount: amount.toFixed(2) } // Assuming amount is in cents
    ]
    setDiscounts(formattedDiscounts)
  }, [loading, cartId, updatedAt])

  return {
    loading,
    updating,
    cartId,
    checkoutUrl,
    empty: quantity === 0,
    items,
    quantity,
    grossTotal,
    actualTotal,
    discounts,
    subscriptionSavings,
    freeShipping,
    cart,
    cartState,
    setCartState,
    products
  }
}

export default useCart
